import React, { useMemo, useCallback } from 'react';
import {
    Field,
    Input,
    ToggleCard,
    TooltipHelp,
    Tooltip,
    ToastContext,
    Form,
    ToggleLabel,
    RadioLabel,
    Icon,
    Modal,
    Button,
    InputLabel,
    FieldErrors,
    Section
} from '@weezevent/nacre';
import PassCultureLogo from '../images/passculture.png';
import { useTranslation, Trans } from 'react-i18next';
import PassCultureEvent from '../../../../models/passCultureEvent';
import Styles from './passCultureToggleCard.module.css';
import useAppContext from '../../../../../src/context/useAppContext';
import { useForm } from '@weezevent/weezjs-core';
import { requiredField } from '../../../../utils/validators';

const doubleBookingAllowedEventTypes = ['seminar', 'concerts', 'festivals', 'shows', 'clubbing', 'students', 'conferences', 'workshops', 'tradeshows', 'test'];

function validateSiret(value) {
    const re = /^\d{14}$/;
    return re.test(value);
}

function isNumber(value) {
    return /^\d*$/.test(value);
}

export function PassCultureAddressCreator({ openModal, setOpenModal, event, organizationId, onSuccess = () => {} }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [displayHelp, setDisplayHelp] = React.useState(false);
    const validators = React.useMemo(() => {
        return {
            street: requiredField,
            postalCode: requiredField,
            city: requiredField,
            lat: requiredField,
            long: requiredField
        };
    }, []);

    const [form, setForm, , formErrors, formValidate] = useForm(
        {
            street: event.address.address1,
            postalCode: event.address.zip_code,
            city: event.address.city,
            lat: event.address.coordinates?.lat,
            long: event.address.coordinates?.long
        },
        validators
    );

    const onSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.event.integration.passculture.venue.add-address-error'));
        }

        PassCultureEvent.createAddresses({
            organizationId: organizationId,
            eventId: event.id,
            street: form.street,
            city: form.city,
            postalCode: form.postalCode,
            lat: form.lat,
            long: form.long
        })
            .then(data => {
                console.warn(data.response);
                onSuccess();
            })
            .catch(() => {
                // do nothing
            })
            .finally(() => {
                setOpenModal(false);
            });
    }, [form, toast, onSuccess]);

    return (
        <Modal
            open={openModal}
            title={t('ticket.event.integration.passculture.venue.add-address-title')}
            subtitle={t('ticket.event.integration.passculture.venue.add-address-description')}
            onClose={() => setOpenModal(false)}
            submitButton={
                <Button
                    primary
                    onClick={() => {
                        onSubmit(true);
                    }}
                    label={t('ticket.common.action.validate')}
                />
            }
            cancelButton={<Button onClick={() => setOpenModal(false)} label={t('ticket.common.action.cancel')} />}
        >
            <div>
                <Field>
                    <InputLabel label={t('ticket.event.integration.passculture.venue.street')} required={true} htmlFor={'pc-street'} />
                    <Input id="street" disabled={true} value={form.street} onChange={(_, { value }) => setForm('street', value)} errors={Boolean(formErrors['street'])} />
                    {formErrors['street'] ? <FieldErrors errors={[t('ticket.common.required')]} /> : null}
                </Field>
                <Field>
                    <InputLabel label={t('ticket.event.integration.passculture.venue.city')} required={true} htmlFor={'pc-city'} />
                    <Input id="city" disabled={true} value={form.city} onChange={(_, { value }) => setForm('city', value)} errors={Boolean(formErrors['city'])} />
                    {formErrors['city'] ? <FieldErrors errors={[t('ticket.common.required')]} /> : null}
                </Field>
                <Field>
                    <InputLabel label={t('ticket.event.integration.passculture.venue.postalCode')} required={true} htmlFor={'pc-postal-code'} />
                    <Input
                        id="postalCode"
                        disabled={true}
                        value={form.postalCode}
                        onChange={(_, { value }) => setForm('postalCode', value)}
                        errors={Boolean(formErrors['postalCode'])}
                    />
                    {formErrors['postalCode'] ? <FieldErrors errors={[t('ticket.common.required')]} /> : null}
                </Field>
                <Field>
                    <div className={Styles['coordinates_title']}>
                        <InputLabel label={t('ticket.event.integration.passculture.venue.coordinates')} required={true} htmlFor={'pc-coordinate'} />

                        <Tooltip
                            arrow
                            title={t('ticket.event.integration.passculture.venue.find_coordinates')}
                            morePlaceholder={t('ticket.event.integration.passculture.venue.find_coordinates_link_placeholder')}
                            moreURL={t('ticket.event.integration.passculture.venue.find_coordinates_link')}
                        >
                            <div onClick={() => setDisplayHelp(!displayHelp)}>
                                <Icon className={Styles['help-icon']} name={'help'} />
                            </div>
                        </Tooltip>
                    </div>
                    <Section className={Styles['coordinates']}>
                        <div className={Styles['lat-long']}>
                            <InputLabel label={t('ticket.event.integration.passculture.venue.lat')} required={true} htmlFor={'pc-lat'} errors={Boolean(formErrors['lat'])} />
                            <Input id="lat" value={form.lat} onChange={(_, { value }) => setForm('lat', value)} />
                            {formErrors['lat'] ? <FieldErrors errors={[t('ticket.common.required')]} /> : null}
                        </div>
                        <div className={Styles['lat-long']}>
                            <InputLabel label={t('ticket.event.integration.passculture.venue.long')} required={true} htmlFor={'pc-long'} errors={Boolean(formErrors['long'])} />
                            <Input id="long" value={form.long} onChange={(_, { value }) => setForm('long', value)} />
                            {formErrors['long'] ? <FieldErrors errors={[t('ticket.common.required')]} /> : null}
                        </div>
                    </Section>
                </Field>
            </div>
        </Modal>
    );
}

export function PassCultureVenueSelector({ venue, address, isSiretValid, updateFormData }) {
    const { t } = useTranslation();
    const [{ organization, event }] = useAppContext();

    const [search] = React.useState({
        street: event.address.address1,
        postalCode: event.address.zip_code,
        city: event.address.city
    });
    const [addresses, setAddresses] = React.useState([]);
    const [openModal, setOpenModal] = React.useState(false);

    const trigger_search = React.useCallback(() => {
        PassCultureEvent.searchAddresses({
            organizationId: organization.id,
            eventId: event.id,
            street: search.street,
            city: search.city,
            postalCode: search.postalCode
        })
            .then(data => {
                setAddresses(data.response.addresses);
            })
            .catch(() => {
                // do nothing
            });
    }, [event, organization, search]);

    React.useEffect(() => {
        if (!isSiretValid) {
            return;
        }
        if (search.street && search.city && search.postalCode) {
            trigger_search();
        }
    }, [event, organization, search, isSiretValid]);

    const pickedAddress = React.useMemo(() => {
        if (!venue) {
            return '';
        }

        if (!address) {
            return `${venue.location.address}, ${venue.location.city}, ${venue.location.postalCode}`;
        }
        return `${search.street}, ${search.city}, ${search.postalCode}`;
    }, [addresses, venue, search, address]);

    if (!isSiretValid || !venue) {
        return <div></div>;
    }

    return (
        <div>
            <PassCultureAddressCreator openModal={openModal} setOpenModal={setOpenModal} event={event} organizationId={organization.id} onSuccess={() => trigger_search()} />
            <label className="wz-input-label" htmlFor="pc-radio-title">
                {t('ticket.event.integration.passculture.venue.radio-title')}
            </label>
            <div className={Styles['radio-input-container']}>
                <RadioLabel name="venue-or-address" value="venue" inline bordered defaultChecked={!address} onClick={() => updateFormData({ venue: venue.id, address: null })}>
                    <div className={Styles['address-container']}>{t('ticket.event.integration.passculture.venue.use-pass-culture-venue')}</div>
                </RadioLabel>
                <Tooltip
                    className={Styles['tooltip-radio-disabled']}
                    triggerOnHover={!addresses.length}
                    visible={!addresses.length}
                    title={t('ticket.event.integration.passculture.venue.event-address-disabled')}
                >
                    <RadioLabel
                        name="venue-or-address"
                        value="address"
                        inline
                        bordered
                        disabled={!addresses.length}
                        defaultChecked={address}
                        onClick={() => updateFormData({ venue: venue.id, address: addresses[0].id })}
                    >
                        <div className={Styles['address-container']}>{t('ticket.event.integration.passculture.venue.use-event-address')}</div>
                    </RadioLabel>
                </Tooltip>
            </div>

            <label className="wz-input-label" htmlFor="pc-address">
                {t('ticket.event.integration.passculture.venue.address')}
            </label>
            <Input id="address" disabled={true} value={pickedAddress} />

            {!addresses.length ? (
                <div className={Styles['link-container']}>
                    <span
                        className={Styles['link']}
                        onClick={() => {
                            setOpenModal(true);
                        }}
                    >
                        <Icon name="external-link" /> {t('ticket.event.integration.passculture.venue.add-address')}{' '}
                    </span>
                </div>
            ) : null}
        </div>
    );
}

export default function PassCultureToggleCard({ organizationId, eventId, eventSubType, eventProvider = null, disabled, setForm, form, formErrors }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const locked = useMemo(() => Boolean(eventProvider?.response.id), [eventProvider]);

    const updateFormData = useCallback(
        data => {
            if (!disabled) setForm({ ...data }, true);
        },
        [disabled]
    );

    const isDoubleBookingCheckedByDefault = useMemo(() => {
        return !eventProvider?.response?.id && doubleBookingAllowedEventTypes.includes(eventSubType);
    }, [eventProvider, eventSubType, doubleBookingAllowedEventTypes]);

    const _onToggle = useCallback(() => {
        if (locked) {
            // If we are locked (because ProviderEvent already exists) do not accept to disactivate it.
            return;
        }

        if (form.is_passculture_activated) {
            updateFormData({
                is_passculture_activated: false,
                siret: eventProvider?.response.siret || '',
                venue: eventProvider?.response.venue || '',
                enableDoubleBookings: eventProvider?.response.is_double_booking_enabled || false
            });
        } else {
            // add siret key in form to prevent save while toggle is true
            updateFormData({ is_passculture_activated: true, ...(isDoubleBookingCheckedByDefault && { enableDoubleBookings: true }) });
        }
    }, [form, eventProvider, isDoubleBookingCheckedByDefault, locked]);

    const getVenues = useCallback(
        siret => {
            PassCultureEvent.getVenues({
                organizationId,
                eventId,
                siret
            })
                .then(({ response }) => {
                    updateFormData({ siret, venues: [response], venue: response.id });
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.event.integration.passculture.venue.not-found'));
                });
        },
        [organizationId, eventId, updateFormData]
    );
    const onSiretChange = useCallback(
        (value, callback) => {
            // if someone smart remove the disabled prop
            if (!isNumber(value) || eventProvider?.response.siret) return;
            updateFormData({ siret: value, venues: [], venue: '' });
            if (validateSiret(value)) {
                // Get venues
                getVenues(value);
            }
        },
        [eventProvider, updateFormData]
    );

    const isSiretValid = useMemo(() => validateSiret(form?.siret), [form]);
    const isDisabled = useMemo(() => {
        return disabled || !isSiretValid || !doubleBookingAllowedEventTypes.includes(eventSubType);
    }, [disabled, isSiretValid, doubleBookingAllowedEventTypes, eventSubType]);

    function HelpTitle() {
        return (
            <>
                {t('ticket.event.integration.passculture.title')}
                <TooltipHelp title={t('ticket.event.integration.passculture.tooltip')} mini position={'top'} />
            </>
        );
    }
    return (
        <ToggleCard
            title={<HelpTitle />}
            subtitle={t('ticket.event.integration.passculture.description')}
            logoURL={PassCultureLogo}
            key={'passculture'}
            toggle={form.is_passculture_activated}
            onToggle={_onToggle}
            disabled={disabled || locked}
        >
            <Field>
                <label className="wz-input-label" htmlFor="pc-siret">
                    {t('ticket.event.integration.passculture.siret.title')}
                </label>
                <Input
                    id="pc-siret"
                    disabled={disabled || eventProvider?.response.siret}
                    maxLength={14}
                    errors={formErrors['siret'] ? [t(formErrors['siret'])] : []}
                    value={form?.siret}
                    onChange={(_, { value }) => onSiretChange(value)}
                    placeholder={t('ticket.event.integration.passculture.siret.placeholder')}
                />
            </Field>
            <Form.FieldErrors errors={formErrors['siret'] ? [t(formErrors['siret'])] : []} />
            <Field>
                <PassCultureVenueSelector venue={form.venues[0]} address={form.address} isSiretValid={isSiretValid} updateFormData={updateFormData} />
            </Field>
            <Field>
                <ToggleLabel
                    disabled={isDisabled}
                    label={
                        <>
                            <Trans i18nKey={'ticket.event.integration.passculture.enableDoubleBookings.title'}></Trans>
                            <TooltipHelp title={t('ticket.event.integration.passculture.enableDoubleBookings.tooltip')} mini position={'top'}></TooltipHelp>
                        </>
                    }
                    checked={form?.enableDoubleBookings}
                    errors={formErrors['enableDoubleBookings'] ? [t(formErrors['enableDoubleBookings'])] : []}
                    onChange={(e, { checked }) => {
                        updateFormData({ enableDoubleBookings: checked });
                    }}
                    placeholder={t('ticket.event.integration.passculture.venue.placeholder')}
                ></ToggleLabel>
            </Field>
        </ToggleCard>
    );
}
